import moment, { Moment } from 'moment'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import Spinner from '../../App/Components/Spinner/Spinner'
import { EmptyState } from '../../App/Components/UIElements/EmptyState/EmptyState'
import { useAppDispatch, useAppSelector } from '../../App/Hooks/reduxHooks'
import DateRangeSelection from '../DateRangeSelection/DateRangeSelection'

import Chart from './Chart'
import { ILabels } from './IProps'
import ReportTable from './ReportTable'
import { getExpensesReport } from './Store/Action'

export const ExpensesReport = () => {
  const dispatch = useAppDispatch()
  const { t } = useTranslation()

  const [from, setFrom] = useState<Moment>(moment().startOf('month'))
  const [to, setTo] = useState<Moment>(moment().endOf('month'))
  const [span, setSpan] = useState<'week' | 'month' | 'year'>('month')
  const [isLoading, setIsLoading] = useState<boolean>(true)

  const { expensesReport } = useAppSelector(state => {
    return { expensesReport: state.reports.expenses.list }
  })

  useEffect(() => {
    fetchData()
  }, [from, to])

  const houseStyleColorArray: string[] = [
    '#ffab0f',
    '#69d321',
    '#8262d8',
    '#a47433',
    '#f8549c',
    '#d24444',
    '#24a7f3',
    '#f8549c',
    '#00ddff',
  ]

  // MAKE THIS FETCH THE REAL DATA IN THE FUTURE
  const fetchData = async () => {
    setIsLoading(true)
    await dispatch(getExpensesReport(from, to))
    setIsLoading(false)
  }

  const getLabels = () => {
    // Make [] of all reasons in data, filter this [] so it only contains unique reasons
    const uniqueReasonTypes = expensesReport
      .map(data => {
        return data.reason
      })
      .filter((value, index, self) => self.indexOf(value) === index)
    // Each unique reasons gets its color, this color comes from a pre defined houseStyleColorArray []
    return uniqueReasonTypes.map((label, index) => {
      const colorForLabel = houseStyleColorArray[index]
      return {
        name: label === null ? t('No reason type') : label,
        color: colorForLabel,
      }
    })
  }
  const getColoredData = (labels: ILabels[]) => {
    // Go over our incoming data -> make same length [] containing data's corresponding labelColor
    const colorArrayForData = expensesReport.map(data => {
      let labelNumber: number = null
      labels?.forEach((label: ILabels, index: number) => {
        label.name === data.reason ? (labelNumber = index) : null
      })
      if (labelNumber !== null) {
        return labels[labelNumber].color
      }
      return
    })
    // Merge color [] with data []
    return colorArrayForData?.map((color, index) => {
      return { ...expensesReport[index], color }
    })
  }

  const getContent = () => {
    if (!expensesReport) {
      return <Spinner />
    }
    const labels = getLabels()
    return (
      <>
        <Chart
          chartLabels={labels}
          results={getColoredData(labels)}
          span={span}
        />
        <ReportTable results={expensesReport} span={span} />
      </>
    )
  }

  return (
    <>
      <h1>{t('Expenses report')}</h1>
      <DateRangeSelection
        onSelected={(selectedSpan, selectedFrom, selectedTo) => {
          setSpan(selectedSpan)
          setFrom(selectedFrom)
          setTo(selectedTo)
        }}
        span={span}
        from={from}
        to={to}
        availableSpans={['week', 'month', 'year']}
      />
      {isLoading ? (
        <Spinner />
      ) : expensesReport?.length > 0 ? (
        getContent()
      ) : (
        <EmptyState guidance={t('There is no data for this date range.')} />
      )}
    </>
  )
}
